<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="900" scrollable>
    <v-card>
      <v-card-title class="dialog-title">
        {{ title }}
        <v-spacer />
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-0">
        <!-- KPI mini-cards -->
        <div v-if="kpiCards && kpiCards.length" class="detail-kpi-cards">
          <div v-for="(kpi, i) in kpiCards" :key="'kpi-'+i" class="detail-kpi-card">
            <v-icon small :color="kpi.color" class="mr-1">{{ kpi.icon }}</v-icon>
            <div class="detail-kpi-content">
              <span class="detail-kpi-value">{{ kpi.value }}</span>
              <span class="detail-kpi-label">{{ kpi.label }}</span>
            </div>
          </div>
        </div>

        <v-data-table
          :headers="headers"
          :items="items"
          :server-items-length="totalItems"
          :options.sync="internalOptions"
          :loading="loading"
          :no-data-text="noDataText || undefined"
          :footer-props="{
            'items-per-page-options': [10, 25, 50],
          }"
          @update:options="$emit('update:options', $event)"
          class="detail-table"
        >
          <!-- Column: name (link or plain) -->
          <template v-slot:item.name="{ item }">
            <a v-if="getColumnLink('name') && item[getColumnLink('name').linkId]" class="detail-link" @click.stop="emitLink('name', item)">{{ item.name }}</a>
            <span v-else>{{ item.name }}</span>
          </template>

          <!-- Column: charger (link or plain) -->
          <template v-slot:item.charger="{ item }">
            <a v-if="getColumnLink('charger')" class="detail-link" @click.stop="emitLink('charger', item)">{{ item.charger }}</a>
            <span v-else>{{ item.charger }}</span>
          </template>

          <!-- Column: status (chip or plain) -->
          <template v-slot:item.status="{ item }">
            <v-chip v-if="isChipColumn('status')" x-small outlined :color="getStatusColor(item.status)" class="detail-status-chip">{{ item.status }}</v-chip>
            <span v-else>{{ item.status }}</span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
const STATUS_COLORS = {
  AVAILABLE: '#00897E',
  CHARGING: '#DEAC3C',
  OFFLINE: '#E53C22',
  BLOCKED: '#b3028d',
  OUTOFORDER: '#758093',
  UNKNOWN: '#9e9e9e',
  PLANNED: '#1586e3',
  MAINTENANCE: '#6277a1',
}

export default {
  name: 'DashboardDetailDialog',
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: '' },
    headers: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    totalItems: { type: Number, default: 0 },
    options: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    kpiCards: { type: Array, default: () => [] },
    noDataText: { type: String, default: '' },
  },

  data() {
    return {
      internalOptions: { ...this.options },
    }
  },

  watch: {
    options: {
      handler(val) {
        this.internalOptions = { ...val }
      },
      deep: true,
    },
  },

  methods: {
    getColumnLink(columnValue) {
      return this.headers.find(h => h.value === columnValue && h.linkType)
    },

    isChipColumn(columnValue) {
      return this.headers.some(h => h.value === columnValue && h.chipStatus)
    },

    emitLink(columnValue, item) {
      const header = this.getColumnLink(columnValue)
      if (header) {
        this.$emit('click:link', { item, linkType: header.linkType, linkId: item[header.linkId] })
      }
    },

    getStatusColor(status) {
      return STATUS_COLORS[(status || '').toUpperCase()] || '#9e9e9e'
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-title {
  font-size: 16px;
  font-weight: 700;
  border-bottom: 1px solid #e5e7eb;
}

.detail-table {
  ::v-deep .v-data-table__wrapper {
    max-height: 500px;
    overflow-y: auto;
  }
}

.detail-link {
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    color: #1565c0;
    text-decoration-thickness: 2px;
  }
}

.detail-status-chip {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.detail-kpi-cards {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.detail-kpi-card {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  flex: 1;
  min-width: 0;
}

.detail-kpi-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.detail-kpi-value {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.detail-kpi-label {
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
